<template>
  <section v-if="hasSomethingToSay" class="voice-ledger-card" data-test="voice-ledger-card">
    <template v-if="hasSpokenToday">
      <p class="ledger-today" data-test="ledger-today">
        <font-awesome-icon icon="microphone" class="ledger-icon" aria-hidden="true" />
        <span class="u-tabular">{{
          $t("voiceLedger.cardTitleToday", { minutes: todayMinutes })
        }}</span>
      </p>
      <p class="ledger-week u-tabular" data-test="ledger-week">
        {{ $t("voiceLedger.cardWeekLine", { minutes: weekMinutes }) }}
        <span class="ledger-practice-days" data-test="ledger-practice-days">{{
          $t("voiceLedger.cardPracticeDays", { days: practiceDays })
        }}</span>
      </p>
    </template>

    <template v-else-if="hasLoadedToday">
      <p class="ledger-zero" data-test="ledger-zero-state">
        <font-awesome-icon icon="microphone" class="ledger-icon" aria-hidden="true" />
        <span>{{ $t("voiceLedger.zeroStateLine", { language: languageLabel }) }}</span>
      </p>
      <button type="button" class="ledger-cta" data-test="ledger-cta" @click="onCta">
        {{ $t(cta.key, cta.params) }}
      </button>
    </template>

    <p v-else class="ledger-unavailable" data-test="ledger-unavailable">
      {{ $t("voiceLedger.statsUnavailable") }}
    </p>

    <router-link
      class="ledger-view-all"
      :to="progressRoute"
      data-test="ledger-view-all"
    >
      {{ $t("voiceLedger.viewAll") }}
      <font-awesome-icon icon="chevron-right" aria-hidden="true" />
    </router-link>
  </section>
</template>

<script>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { spokenStats, initSpokenStats } from "@/store/spokenStats";
import { resolveNextAction } from "@/scripts/nextAction.js";
import { Routes } from "@/constants/routes.js";
import { languageName } from "@/data/Language.js";

// The Voice Ledger's home-screen surface: how long the learner has actually
// spoken today, and one action that gets them speaking again. The full set of
// ranges lives on the Progress page.
//
// Spec: docs/superpowers/specs/2026-07-23-voice-ledger-design.md
export default {
  name: "VoiceLedgerCard",
  props: {
    /** The active course node, for the CTA's smart route. */
    courseData: { type: Object, default: null },
    /** Code of the language being learned, e.g. "es-mx". */
    languageCode: { type: String, default: null },
    /**
     * Coordinates ("{unit}-{week}-{lesson}") of lessons whose start is still in
     * flight: the client wrote dialog-meta and the server has not stamped the
     * lesson node yet. LessonsView renders those tiles as in progress off the
     * same list, so the CTA has to say "continue" for them too — a card reading
     * "start your lesson" over an in-progress tile invites the second tap the
     * in-flight record exists to prevent.
     */
    inflightLessonIds: { type: Array, default: () => [] },
  },
  emits: ["select-lesson"],
  setup(props, { emit }) {
    const router = useRouter();
    // Subscribing from the card rather than from LessonsView keeps the store
    // idle while the voiceLedger flag is off: nothing mounts, nothing reads.
    initSpokenStats();

    // The store follows its own listener on the selected language, and the
    // view follows another. During a switch they disagree for a beat, and a
    // card that rendered anyway would caption the new language's empty
    // buckets with the old language's name: "You haven't spoken Español yet
    // today" to someone who just spoke Español for ten minutes.
    const isCurrentLanguage = computed(
      () => Boolean(props.languageCode) && spokenStats.language === props.languageCode
    );
    const languageLabel = computed(() => languageName(props.languageCode));

    // Only `loaded` proves a zero is a real zero. The store deliberately
    // zeroes every calendar range while the accounting timezone is unknown,
    // and rendering the invitation off that would tell a learner who did
    // practice that they had not.
    const hasSpokenToday = computed(
      () => isCurrentLanguage.value && spokenStats.loaded && spokenStats.ranges.todaySeconds > 0
    );
    const hasLoadedToday = computed(() => isCurrentLanguage.value && spokenStats.loaded);
    // `error` covers the lifetime read too, which is an all-time number this
    // card never renders, so it only downgrades the card when the buckets
    // themselves are missing.
    const hasSomethingToSay = computed(
      () => hasLoadedToday.value || (isCurrentLanguage.value && spokenStats.error)
    );

    // 1-59 seconds of speech is not zero minutes of effort. The floor applies
    // to today alone: a week total is a sum the learner can check against the
    // Progress page, and inflating it would not survive that comparison.
    const todayMinutes = computed(() =>
      Math.max(1, Math.floor(spokenStats.ranges.todaySeconds / 60))
    );
    const weekMinutes = computed(() => Math.floor(spokenStats.ranges.weekSeconds / 60));
    const practiceDays = computed(() => spokenStats.ranges.practiceDaysThisWeek);

    const nextAction = computed(() =>
      resolveNextAction(props.courseData, props.inflightLessonIds)
    );
    const cta = computed(() => {
      const action = nextAction.value;
      if (action.type === "continueLesson") {
        return {
          key: "voiceLedger.ctaContinueLesson",
          params: { lessonTitle: action.lessonTitle },
        };
      }
      if (action.type === "startLesson") {
        return { key: "voiceLedger.ctaStartLesson", params: {} };
      }
      return { key: "voiceLedger.ctaFreestyle", params: {} };
    });

    const onCta = () => {
      const action = nextAction.value;
      if (action.type === "freestyle") {
        router.push(Routes.DASHBOARD_CHAT);
        return;
      }
      // The card lives on LessonsView, which owns lesson opening (dialog
      // creation, unlocking, toasts). Handing the resolved lesson back to its
      // existing handler beats duplicating any of that here.
      emit("select-lesson", action.lesson, action.unitNumber, action.weekNumber);
    };

    return {
      languageLabel,
      hasLoadedToday,
      hasSpokenToday,
      hasSomethingToSay,
      todayMinutes,
      weekMinutes,
      practiceDays,
      cta,
      onCta,
      progressRoute: Routes.DASHBOARD_PROGRESS,
    };
  },
};
</script>

<style scoped>
.voice-ledger-card {
  display: flex;
  flex-direction: column;
  gap: var(--al-space-sm);
  padding: var(--al-space-md);
  margin-bottom: var(--al-space-md);
  background: var(--al-surface-raised);
  border: 1px solid var(--al-border);
  border-radius: var(--al-radius-md);
  box-shadow: var(--al-shadow-sm);
}

.ledger-today,
.ledger-zero {
  display: flex;
  align-items: baseline;
  gap: var(--al-space-sm);
  margin: 0;
  font-size: 18px;
  line-height: 1.5;
  font-weight: 600;
  color: var(--al-text);
}

.ledger-zero {
  font-weight: 400;
}

.ledger-icon {
  color: var(--al-primary-fill);
}

.ledger-week {
  display: flex;
  flex-wrap: wrap;
  gap: var(--al-space-xs) var(--al-space-sm);
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: var(--al-text-muted);
}

.ledger-practice-days::before {
  content: "·";
  margin-right: var(--al-space-sm);
}

.ledger-unavailable {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: var(--al-text-muted);
}

.ledger-cta {
  min-height: 44px;
  padding: var(--al-space-sm) var(--al-space-md);
  background: var(--al-primary-fill);
  color: var(--al-white);
  border: none;
  border-radius: var(--al-radius-md);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background var(--al-dur-short) var(--al-ease-move);
}

.ledger-cta:hover,
.ledger-cta:focus-visible {
  background: var(--al-primary-hover);
}

.ledger-view-all {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: var(--al-space-xs);
  min-height: 44px;
  font-size: 14px;
  font-weight: 600;
  color: var(--al-primary-fill);
  text-decoration: none;
}

.ledger-view-all:hover,
.ledger-view-all:focus-visible {
  text-decoration: underline;
}

/* The card sits at the top of a scrolling list on phones, so it stays
   compact there and only relaxes once there is room. */
@media (min-width: 768px) {
  .voice-ledger-card {
    padding: var(--al-space-lg);
    gap: var(--al-space-md);
  }

  /* Full width is right for a thumb on a phone; across a desktop card it
     reads as a banner rather than a button. */
  .ledger-cta {
    align-self: flex-start;
    padding-left: var(--al-space-lg);
    padding-right: var(--al-space-lg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ledger-cta {
    transition: none;
  }
}
</style>
